/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IImageProps, Image } from '@fluentui/react/lib/Image';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';

export type EmptyStateProps = {
    imageName?: string;
    message?: React.ReactNode;
    trainingPageLink?: string;
    imageStyles?: IImageProps['styles'];
};

const messages = defineMessages({
    emptyStateMessage: {
        id: 'emptyProjectsListView.emptyStateMessage',
        defaultMessage: `Click 'Create a project' to get started.`
    },
    emptyStateImageAltText: {
        id: 'emptyProjectsListView.emptyStateImageAltText',
        defaultMessage: 'Empty state image.'
    }
});

const theme = getScopedTheme('EmptyState');
const textStyles = classNamesFunction<ITextProps, ITextStyles>()({ root: { padding: '16px' } });

export const EmptyProjectsListView = observer((props: EmptyStateProps) => {
    const { message: emptyStateMsg, imageStyles, imageName: emptyStateImgName = 'newEmptyState' } = props;

    const { localize } = useLocalization();
    const imageSrc = getAssetsUrl(`assets/images/customText/${emptyStateImgName}.svg`);

    return (
        <Stack
            verticalFill
            verticalAlign="center"
            horizontalAlign="center"
            tokens={{ childrenGap: 42 }}
            style={{ backgroundColor: theme.backgroundColor }}>
            <Image styles={imageStyles} src={imageSrc} alt={localize(messages.emptyStateImageAltText)} />
            <LuisText styles={textStyles}>{emptyStateMsg ?? localize(messages.emptyStateMessage)}</LuisText>
        </Stack>
    );
});
